import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin, MapPinned, Facebook, Instagram, Youtube } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();



  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=100066652312267', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/gastrocare.in/', label: 'Instagram' },
    { icon: Youtube, href: 'https://www.youtube.com/@drsanjaykumargastrocare', label: 'Youtube' },
  ];

  return (
    <footer className="bg-slate-900 text-white pt-16 pb-8" data-testid="footer">
      <div className="section-container">

        {/* ── 3-column grid ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-8 p-2">


          {/* ── 1. Brand & About ─────────────────────────────────────────── */}
          <div>
            {/* Logo */}
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-[#800000] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">GC</span>
              </div>
              <div>
                <p className="font-bold text-white text-lg leading-tight">GastroCare</p>
                <p className="text-xs text-slate-400">Obesity Cure</p>
              </div>
            </div>

            {/* Tagline */}
            <p className="text-slate-400 mb-6 leading-relaxed text-sm">
              {t('footer_tagline')}
            </p>

            {/* Social Media Icons */}
            <div className="flex items-center gap-3 mt-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-800 rounded-full hover:bg-[#800000] transition-colors duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5 text-white" />
                </a>
              ))}
            </div>
          </div>


          {/* ── 3. Contact Details ───────────────────────────────────────── */}
          <div>
            <h4 className="font-bold text-white mb-4">Contact Us</h4>
            <ul className="space-y-3">

              {/* Phone */}
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#800000] flex-shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <a href="tel:+917554055123" className="text-slate-400 hover:text-[#800000] text-sm transition-colors">
                    {t('contact_phone1')}
                  </a>
                  <a href="tel:+919300951215" className="text-slate-400 hover:text-[#800000] text-sm transition-colors">
                    +91 93009 51215
                  </a>
                </div>
              </li>

              {/* Email */}
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#800000] flex-shrink-0" />
                <a
                  href="mailto:contact@bigbariatrics.com"
                  className="text-slate-400 hover:text-[#800000] text-sm transition-colors break-all"
                >
                  contact@bigbariatrics.com
                </a>
              </li>

              {/* Address */}
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#800000] flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm leading-relaxed">
                  120-121, Main Rd No. 3, near Personal Branch SBI,
                  E-3, Arera Colony, Bhopal, Madhya Pradesh 462016
                </span>
              </li>

            </ul>
          </div>

          {/* ── 4. Google Map ─────────────────────────────────────────────── */}
          <div>
            {/* Section title */}
            <h4 className="font-bold text-white mb-4 flex items-center gap-2">
              <MapPinned className="w-4 h-4 text-[#800000]" />
              Find Us
            </h4>

            {/* Premium map card — wrapper div controls height, iframe fills it */}
            <div className="rounded-2xl overflow-hidden border border-slate-700 shadow-xl bg-slate-800 transition-all duration-300 hover:-translate-y-[3px] hover:shadow-2xl h-[200px] sm:h-[220px] lg:h-[240px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d6044.557235568076!2d77.437592!3d23.212486!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397c424dbb83ce07%3A0x98a86ce4c0e19e8d!2sGastrocare%20%2C%20Liver%20%26%20Digestive%20Disease%20Center!5e1!3m2!1sen!2sin!4v1774955279039!5m2!1sen!2sin"
                width="100%"
                height="100%"
                className="w-full h-full"
                style={{ border: 0, display: 'block' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="GastroCare Bhopal Location"
              />
            </div>
          </div>

        </div>

        {/* ── Divider + Copyright ─────────────────────────────────────────── */}
        <div className="border-t border-slate-800 pt-6">
          <div className="flex flex-col items-center justify-center gap-2">
            <p className="text-slate-500 text-sm text-center">
              © {new Date().getFullYear()} GastroCare Bhopal. {t('footer_rights')}
            </p>

            <p className="text-slate-500 text-xs text-center">
              Developed by
              <span>
                <a
                  href="https://mistpl.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#800000] hover:underline ml-1"
                >
                  MISTPL
                </a>
              </span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;